/* A gentle word on the day: somebody you care about is overdue.
 *
 * One person, at most once a day, and only when there is something to do about
 * it — a free time in the next few days. The list on People holds everybody;
 * this is the one sentence worth putting in front of today.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, readLocal, writeLocal } from '../api';
import Icon from './Icon';
import { useFriends, PlanFriend } from '../screens/people/Friends';
import type { FreeSlot } from '../screens/people/Friends';
import { friendlyDate } from './time';

const RANK = { favourite: 0, regular: 1, occasional: 2 } as const;

export default function FriendNudge({ today }: { today: string }) {
  const key = `amon.friendNudge.${today}`;
  const [hidden, setHidden] = useState(() => readLocal(key) === '1');
  const [planning, setPlanning] = useState(false);
  const friends = useFriends();

  const who = (friends.data ?? [])
    .filter(f => (f.state === 'overdue' || f.state === 'due') && f.closeness !== 'occasional')
    .sort((a, b) => RANK[a.closeness] - RANK[b.closeness] || a.daysLeft - b.daysLeft)[0];

  const times = useQuery({
    queryKey: ['friend-times', who?.id, today],
    enabled: !!who && !hidden,
    queryFn: async () => {
      const r = await get<{ times: FreeSlot[] }>(`/api/friends/${who!.id}/times?from=${today}&days=5`);
      return r.ok ? r.times : [];
    }
  });

  if (hidden || !who) return null;
  const next = times.data?.[0];
  if (!next) return null;

  const dismiss = () => { writeLocal(key, '1'); setHidden(true); };

  return (
    <div className="notice friend-nudge" role="status">
      <span className="notice-icon"><Icon name="hobbies" size={18} /></span>
      <div>
        <p>
          <strong>
            {who.daysSince != null
              ? `It's been ${who.daysSince} days since you saw ${who.name}`
              : `You haven't planned anything with ${who.name} yet`}
          </strong>
        </p>
        <p className="muted small">
          {next.bothFree ? 'You are both free' : 'You are free'} {friendlyDate(next.date)} at {next.start}.
        </p>
      </div>
      <div className="inline">
        <button className="btn btn-primary btn-sm" onClick={() => setPlanning(true)}>
          <Icon name="calendar" size={15} />Plan it
        </button>
        <button className="btn btn-ghost btn-icon" onClick={dismiss} aria-label="Not today" title="Not today">
          <Icon name="close" size={16} />
        </button>
      </div>
      {planning && <PlanFriend friend={who} onClose={() => setPlanning(false)} />}
    </div>
  );
}
